import type { CustomIndicator, PineJS } from '../types/charting-library';

interface PineSeries {
  get(index?: number): number;
}

interface PineContext {
  new_var(value: number): PineSeries;
  setMinimumAdditionalDepth(depth: number): void;
}

type IndicatorOutput = Array<number | { value: number; offset?: number }>;

interface EmaConstructor {
  _context?: PineContext;
  _input?: (index: number) => unknown;
  main(context: PineContext, inputCallback: (index: number) => unknown): [number];
}

interface IchimokuWeeklyConstructor {
  _context?: PineContext;
  _input?: (index: number) => unknown;
  main(context: PineContext, inputCallback: (index: number) => unknown): IndicatorOutput;
}

interface IchimokuWeeklyAverageConstructor extends IchimokuWeeklyConstructor {
  _avgHighs?: number[];
  _avgLows?: number[];
  _lastTime?: number;
}

const START_DATE_DEFAULT = Date.parse('2021-01-01T00:00:00+07:00');

const ICHIMOKU_COLORS = {
  up: 0,
  down: 1,
  yDown: 2,
  yUp: 3,
  ybDown: 4,
  ybUp: 5,
  bUp: 6,
  bDown: 7,
} as const;

const ICHIMOKU_SIGNALS = {
  default: -1,
  yDown: 0,
  yUp: 1,
  bDown: 2,
  bUp: 3,
  ybDown: 4,
  ybUp: 5,
} as const;

const ICHIMOKU_COLOR_STYLES = [
  { id: 'candles_up', title: 'Up', value: ICHIMOKU_COLORS.up, color: '#5b9cf6' },
  { id: 'candles_down', title: 'Down', value: ICHIMOKU_COLORS.down, color: '#b2b5be' },
  { id: 'candles_y_down', title: 'Y Down', value: ICHIMOKU_COLORS.yDown, color: '#f23645' },
  { id: 'candles_y_up', title: 'Y Up', value: ICHIMOKU_COLORS.yUp, color: '#ffb253' },
  { id: 'candles_yb_down', title: 'YB Down', value: ICHIMOKU_COLORS.ybDown, color: '#2b7b7b' },
  { id: 'candles_yb_up', title: 'YB Up', value: ICHIMOKU_COLORS.ybUp, color: '#53ffff' },
  { id: 'candles_b_up', title: 'B Up', value: ICHIMOKU_COLORS.bUp, color: '#2962ff' },
  { id: 'candles_b_down', title: 'B Down', value: ICHIMOKU_COLORS.bDown, color: '#8e21f3' },
] as const;

export const CHARTIFY_EMA_INDICATOR_NAME = 'Chartify EMA';
export const ICHIMOKU_WEEKLY_INDICATOR_NAME = 'Ichimoku Weekly';
export const ICHIMOKU_WEEKLY_AVERAGE_INDICATOR_NAME = 'Ichimoku Weekly Average';

export const CUSTOM_INDICATOR_NAMES = [
  CHARTIFY_EMA_INDICATOR_NAME,
  ICHIMOKU_WEEKLY_INDICATOR_NAME,
  ICHIMOKU_WEEKLY_AVERAGE_INDICATOR_NAME,
] as const;

export function getCustomIndicators(PineJS: PineJS): Promise<readonly CustomIndicator[]> {
  return Promise.resolve([
    buildChartifyEma(PineJS),
    buildIchimokuWeekly(PineJS),
    buildIchimokuWeeklyAverage(PineJS),
  ]);
}

function buildChartifyEma(PineJS: PineJS): CustomIndicator {
  return {
    name: CHARTIFY_EMA_INDICATOR_NAME,
    metainfo: {
      _metainfoVersion: 52,
      isTVScript: false,
      isTVScriptStub: false,
      is_hidden_study: false,
      is_price_study: true,
      isCustomIndicator: true,
      id: 'ChartifyEMA@chartify-custom-1',
      scriptIdPart: '',
      name: CHARTIFY_EMA_INDICATOR_NAME,
      description: CHARTIFY_EMA_INDICATOR_NAME,
      shortDescription: 'Chartify EMA',
      plots: [{ id: 'plot_0', type: 'line' }],
      defaults: {
        styles: {
          plot_0: {
            linestyle: 0,
            linewidth: 2,
            plottype: 0,
            trackPrice: false,
            transparency: 0,
            visible: true,
            color: '#22c55e',
          },
        },
        inputs: { length: 21 },
      },
      styles: {
        plot_0: { title: 'EMA', histogramBase: 0, joinPoints: false },
      },
      inputs: [
        { id: 'length', name: 'Length', defval: 21, type: 'integer', min: 1, max: 1000 },
      ],
      format: { type: 'inherit' },
    },
    constructor: function (this: EmaConstructor) {
      this.main = function (context, inputCallback) {
        this._context = context;
        this._input = inputCallback;

        const rawLength = this._input(0);
        const length = typeof rawLength === 'number' ? rawLength : 21;
        this._context.setMinimumAdditionalDepth(length);

        const close = PineJS.Std.close(this._context);
        const closeSeries = this._context.new_var(close);
        return [PineJS.Std.ema(closeSeries, length, this._context)];
      };
    },
  };
}

function buildIchimokuWeekly(PineJS: PineJS): CustomIndicator {
  return {
    name: ICHIMOKU_WEEKLY_INDICATOR_NAME,
    metainfo: buildIchimokuColorCandlesMetainfo(
      'IchimokuWeeklyLine@chartify-custom-8',
      ICHIMOKU_WEEKLY_INDICATOR_NAME,
      true,
    ),
    constructor: function (this: IchimokuWeeklyConstructor) {
      this.main = function (context, inputCallback) {
        this._context = context;
        this._input = inputCallback;
        this._context.setMinimumAdditionalDepth(5);

        const startTime = readStartTime(this._input(0));
        const open = PineJS.Std.open(this._context);
        const high = PineJS.Std.high(this._context);
        const low = PineJS.Std.low(this._context);
        const close = PineJS.Std.close(this._context);
        const time = PineJS.Std.time(this._context);
        const normalizedStartTime = normalizeStartTime(startTime, time);

        const highSeries = this._context.new_var(high);
        const lowSeries = this._context.new_var(low);

        let signal: number = ICHIMOKU_SIGNALS.default;

        if (high > highSeries.get(1) && low < lowSeries.get(1)) {
          if (close < open) {
            signal = ICHIMOKU_SIGNALS.yDown;
          } else {
            signal = ICHIMOKU_SIGNALS.yUp;
          }
        }

        if (Number.isFinite(normalizedStartTime)) {
          const highest = Math.max(highSeries.get(1), highSeries.get(2), highSeries.get(3));
          const lowest = Math.min(lowSeries.get(1), lowSeries.get(2), lowSeries.get(3));
          const previousHighest = Math.max(highSeries.get(2), highSeries.get(3), highSeries.get(4));
          const previousLowest = Math.min(lowSeries.get(2), lowSeries.get(3), lowSeries.get(4));
          const trendUp = isThreePointUp(lowSeries.get(1), lowSeries.get(2), lowSeries.get(3), highSeries.get(1), highSeries.get(2), highSeries.get(3));
          const trendDown = isThreePointDown(highSeries.get(1), highSeries.get(2), highSeries.get(3), lowSeries.get(1), lowSeries.get(2), lowSeries.get(3));
          const isStagnating = !trendUp && !trendDown;

          if (close >= open && (isStagnating || signal === ICHIMOKU_SIGNALS.yUp) && high > highest && highSeries.get(1) <= previousHighest) {
            signal = signal === ICHIMOKU_SIGNALS.yUp ? ICHIMOKU_SIGNALS.ybUp : ICHIMOKU_SIGNALS.bUp;
          }
          if (close < open && (isStagnating || signal === ICHIMOKU_SIGNALS.yDown) && low < lowest && lowSeries.get(1) >= previousLowest) {
            signal = signal === ICHIMOKU_SIGNALS.yDown ? ICHIMOKU_SIGNALS.ybDown : ICHIMOKU_SIGNALS.bDown;
          }
        }

        return [
          open,
          high,
          low,
          close,
          getSignalColor(signal, close >= open),
          getSignalColor(signal, close >= open),
          getSignalColor(signal, close >= open),
        ];
      };
    },
  };
}

function buildIchimokuWeeklyAverage(PineJS: PineJS): CustomIndicator {
  return {
    name: ICHIMOKU_WEEKLY_AVERAGE_INDICATOR_NAME,
    metainfo: buildIchimokuColorCandlesMetainfo(
      'IchimokuWeeklyAverageLine@chartify-custom-5',
      ICHIMOKU_WEEKLY_AVERAGE_INDICATOR_NAME,
      false,
    ),
    constructor: function (this: IchimokuWeeklyAverageConstructor) {
      this._avgHighs = [];
      this._avgLows = [];

      this.main = function (context, inputCallback) {
        this._context = context;
        this._input = inputCallback;
        this._context.setMinimumAdditionalDepth(4);

        const high = PineJS.Std.high(this._context);
        const low = PineJS.Std.low(this._context);
        const open = PineJS.Std.open(this._context);
        const close = PineJS.Std.close(this._context);
        const time = PineJS.Std.time(this._context);

        const highSeries = this._context.new_var(high);
        const lowSeries = this._context.new_var(low);
        const avgHighs = this._avgHighs ?? [];
        const avgLows = this._avgLows ?? [];
        this._avgHighs = avgHighs;
        this._avgLows = avgLows;

        if (this._lastTime !== undefined && time < this._lastTime) {
          avgHighs.length = 0;
          avgLows.length = 0;
        }
        this._lastTime = time;

        const prevAvgHigh = avgHighs.at(-1);
        const prevAvgLow = avgLows.at(-1);
        let avgHigh = Number.NaN;
        let avgLow = Number.NaN;

        if (prevAvgHigh === undefined || prevAvgLow === undefined) {
          avgHigh = averageWithFallback(high, highSeries.get(1));
          avgLow = averageWithFallback(low, lowSeries.get(1));
        } else {
          avgHigh = averageWithFallback(high, prevAvgHigh);
          avgLow = averageWithFallback(low, prevAvgLow);
        }

        avgHighs.push(avgHigh);
        avgLows.push(avgLow);

        let color: number = close >= open ? ICHIMOKU_COLORS.up : ICHIMOKU_COLORS.down;
        if (!Number.isFinite(avgHigh) || !Number.isFinite(avgLow)) {
          return [Number.NaN, Number.NaN, Number.NaN, Number.NaN, color, color, color];
        }

        if (prevAvgHigh !== undefined && prevAvgLow !== undefined && avgHigh > prevAvgHigh && avgLow < prevAvgLow) {
          color = close < open ? ICHIMOKU_COLORS.yDown : ICHIMOKU_COLORS.yUp;
        }

        if (avgHighs.length >= 4 && avgLows.length >= 4) {
          const high1 = avgHighs[avgHighs.length - 2] ?? Number.NaN;
          const high2 = avgHighs[avgHighs.length - 3] ?? Number.NaN;
          const high3 = avgHighs[avgHighs.length - 4] ?? Number.NaN;
          const low1 = avgLows[avgLows.length - 2] ?? Number.NaN;
          const low2 = avgLows[avgLows.length - 3] ?? Number.NaN;
          const low3 = avgLows[avgLows.length - 4] ?? Number.NaN;
          const highest = Math.max(high1, high2, high3);
          const lowest = Math.min(low1, low2, low3);
          const trendUp = isThreePointUp(low1, low2, low3, high1, high2, high3);
          const trendDown = isThreePointDown(high1, high2, high3, low1, low2, low3);
          const isStagnating = !trendUp && !trendDown;

          if (close >= open && (isStagnating || color === ICHIMOKU_COLORS.yUp) && avgHigh > highest && high1 <= highest) {
            color = mergeYBColor(color, ICHIMOKU_COLORS.bUp);
          }
          if (close < open && (isStagnating || color === ICHIMOKU_COLORS.yDown) && avgLow < lowest && low1 >= lowest) {
            color = mergeYBColor(color, ICHIMOKU_COLORS.bDown);
          }
        }

        return [
          avgLow,
          avgHigh,
          avgLow,
          avgHigh,
          color,
          color,
          color,
        ];
      };
    },
  };
}

function buildIchimokuColorCandlesMetainfo(id: string, name: string, overlay: boolean) {
  return {
    _metainfoVersion: 52,
    isTVScript: false,
    isTVScriptStub: false,
    is_hidden_study: false,
    is_price_study: overlay,
    isCustomIndicator: true,
    id,
    scriptIdPart: '',
    name,
    description: name,
    shortDescription: name,
    plots: [
      { id: 'candles_open', type: 'ohlc_open', target: 'candles' },
      { id: 'candles_high', type: 'ohlc_high', target: 'candles' },
      { id: 'candles_low', type: 'ohlc_low', target: 'candles' },
      { id: 'candles_close', type: 'ohlc_close', target: 'candles' },
      { id: 'candles_color', type: 'ohlc_colorer', target: 'candles', palette: 'ichimokuPalette' },
      { id: 'candles_wick', type: 'wick_colorer', target: 'candles', palette: 'ichimokuPalette' },
      { id: 'candles_border', type: 'border_colorer', target: 'candles', palette: 'ichimokuPalette' },
    ],
    ohlcPlots: {
      candles: { title: 'Candles', showLast: false },
    },
    palettes: {
      ichimokuPalette: {
        colors: Object.fromEntries(ICHIMOKU_COLOR_STYLES.map((style) => [style.value, { name: style.title }])),
      },
    },
    styles: {
      candles_open: { title: 'Open', histogramBase: 0, joinPoints: false, isHidden: true },
      candles_high: { title: 'High', histogramBase: 0, joinPoints: false, isHidden: true },
      candles_low: { title: 'Low', histogramBase: 0, joinPoints: false, isHidden: true },
      candles_close: { title: 'Close', histogramBase: 0, joinPoints: false, isHidden: true },
      candles_color: { title: 'Color', histogramBase: 0, joinPoints: false, isHidden: true },
      candles_wick: { title: 'Wick', histogramBase: 0, joinPoints: false, isHidden: true },
      candles_border: { title: 'Border', histogramBase: 0, joinPoints: false, isHidden: true },
    },
    defaults: {
      styles: {
        candles_open: hiddenOhlcPlotStyle('#5b9cf6'),
        candles_high: hiddenOhlcPlotStyle('#5b9cf6'),
        candles_low: hiddenOhlcPlotStyle('#5b9cf6'),
        candles_close: hiddenOhlcPlotStyle('#5b9cf6'),
        candles_color: hiddenOhlcPlotStyle('#5b9cf6'),
        candles_wick: hiddenOhlcPlotStyle('#5b9cf6'),
        candles_border: hiddenOhlcPlotStyle('#5b9cf6'),
      },
      ohlcPlots: {
        candles: {
          zorder: 100,
          plottype: 'ohlc_candles',
          visible: true,
          color: '#5b9cf6',
          wickColor: '#5b9cf6',
          borderColor: '#5b9cf6',
          drawWick: true,
          drawBorder: true,
        },
      },
      palettes: {
        ichimokuPalette: {
          colors: Object.fromEntries(ICHIMOKU_COLOR_STYLES.map((style) => [style.value, { color: style.color, width: 1, style: 0 }])),
        },
      },
      inputs: { startDate: START_DATE_DEFAULT },
    },
    inputs: [
      { id: 'startDate', name: 'Date', defval: START_DATE_DEFAULT, type: 'time' },
    ],
    format: { type: 'inherit' },
  };
}

function readStartTime(value: unknown): number {
  return typeof value === 'number' && Number.isFinite(value) ? value : START_DATE_DEFAULT;
}

function normalizeStartTime(startTime: number, chartTime: number): number {
  if (chartTime < 10_000_000_000 && startTime > 10_000_000_000) return startTime / 1000;
  return startTime;
}

function average(a: number, b: number): number {
  return (a + b) / 2;
}

function averageWithFallback(a: number, b: number): number {
  return Number.isFinite(b) ? average(a, b) : a;
}

function isThreePointUp(low1: number, low2: number, low3: number, high1: number, high2: number, high3: number): boolean {
  return low1 > low2 && low1 > low3 && low2 > low3 && high1 > high2 && high1 > high3 && high2 > high3;
}

function isThreePointDown(high1: number, high2: number, high3: number, low1: number, low2: number, low3: number): boolean {
  return high1 < high2 && high1 < high3 && high2 < high3 && low1 < low2 && low1 < low3 && low2 < low3;
}

function mergeYBColor(currentColor: number, fallbackColor: number): number {
  if (currentColor === ICHIMOKU_COLORS.yDown) return ICHIMOKU_COLORS.ybDown;
  if (currentColor === ICHIMOKU_COLORS.yUp) return ICHIMOKU_COLORS.ybUp;
  return fallbackColor;
}

function getSignalColor(signal: number, isUp: boolean): number {
  if (signal === ICHIMOKU_SIGNALS.yDown) return ICHIMOKU_COLORS.yDown;
  if (signal === ICHIMOKU_SIGNALS.yUp) return ICHIMOKU_COLORS.yUp;
  if (signal === ICHIMOKU_SIGNALS.bDown) return ICHIMOKU_COLORS.bDown;
  if (signal === ICHIMOKU_SIGNALS.bUp) return ICHIMOKU_COLORS.bUp;
  if (signal === ICHIMOKU_SIGNALS.ybDown) return ICHIMOKU_COLORS.ybDown;
  if (signal === ICHIMOKU_SIGNALS.ybUp) return ICHIMOKU_COLORS.ybUp;
  return isUp ? ICHIMOKU_COLORS.up : ICHIMOKU_COLORS.down;
}

function hiddenOhlcPlotStyle(color: string) {
  return {
    color,
    display: 0,
    linestyle: 0,
    linewidth: 1,
    plottype: 0,
    trackPrice: false,
    transparency: 0,
    visible: true,
  };
}
